import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Card from '../../../components/common/Card';
import Badge from '../../../components/common/Badge';
import Alert from '../../../components/common/Alert';
import Button from '../../../components/common/Button';
import { RefreshCw, AlertCircle, Info } from 'lucide-react';
import { supabase } from '../../../config/supabase';
import { useOrganization } from '../../../hooks/useOrganization';
import { useAuth } from '../../../context/AuthContext';
import { adminAPI } from '../../../services/adminAPI';
import Tooltip from '../../../components/common/Tooltip';

const EMPLOYEE_RANGES = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1000+'
];

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Manufacturing',
  'Retail',
  'Other'
];

export default function DomainSettings() {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const { user } = useAuth();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    employee_count_range: ''
  });

  // Fetch organization details including Google Workspace admin info
  const { data: orgDetails, isLoading: isLoadingOrgDetails } = useQuery({
    queryKey: ['organizationDetails', organization?.id],
    queryFn: async () => {
      if (!organization?.id) {
        return { google_workspace_admin_user_id: null, google_admin_refresh_token: null };
      }

      try {
        const response = await adminAPI.getOrganizationDetails();
        console.log("Organization details response:", response);
        return response.data || {};
      } catch (error) {
        console.error('Failed to fetch organization details:', error);
        // Return empty object instead of throwing to prevent UI errors
        return { google_workspace_admin_user_id: null, google_admin_refresh_token: null };
      }
    },
    enabled: !!organization?.id,
    // Use a longer staleTime since this data doesn't change often
    staleTime: 5 * 60 * 1000
  });

  // Fetch organization dashboard data
  const { data: orgData, isLoading } = useQuery({
    queryKey: ['organizationDashboard', organization?.id],
    queryFn: async () => {
      if (!organization?.id) {
        throw new Error('No organization ID');
      }

      console.log('Fetching data for organization:', organization.id);

      // First get the organization details
      const { data: dashboardData, error: dashboardError } = await supabase
        .from('organization_dashboard_view')
        .select('*')
        .eq('organization_id', organization.id)
        .single();

      if (dashboardError) {
        console.error('Dashboard error:', dashboardError);
        throw dashboardError;
      }

      console.log('Dashboard data:', dashboardData);

      return dashboardData;
    },
    enabled: !!organization?.id
  });

  // Update form data when orgData changes
  useEffect(() => {
    if (orgData) {
      setFormData({
        name: orgData.name || '',
        industry: orgData.industry || '',
        employee_count_range: orgData.employee_count_range || ''
      });
    }
  }, [orgData]);

  // Update organization mutation
  const updateMutation = useMutation({
    mutationFn: async (updates) => {
      const { data, error } = await supabase
        .from('organizations')
        .update(updates)
        .eq('id', organization?.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['organizationDashboard']);
      setError(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    },
    onError: (error) => {
      setError(error.message);
    }
  });

  // Sync Google Workspace Group mutation
  const syncGoogleWorkspaceGroup = useMutation({
    mutationFn: async () => {
      if (!organization?.id) throw new Error('No organization ID');
      
      // Extract domain from email if available, otherwise use user input
      const domain = orgData?.domain_name || '';
      
      return await adminAPI.syncGoogleWorkspaceUsersGroup(organization.id, domain);
    },
    onSuccess: (response) => {
      const { groupEmail, results } = response.data;
      setSyncMessage({
        type: 'success',
        message: `Successfully synced ${results.success} users to Google Workspace group: ${groupEmail}`
      });
      setTimeout(() => setSyncMessage(null), 5000);
    },
    onError: (error) => {
      // Look for specific error messages
      if (error.message?.includes('Insufficient Permission')) {
        setSyncMessage({
          type: 'error',
          message: 'You do not have permission to manage Google Groups. Please log out, then log in again and grant all requested permissions.'
        });
      } else {
        setSyncMessage({
          type: 'error',
          message: error.message || 'Failed to sync Google Workspace group'
        });
      }
    }
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  // Check if Google Workspace integration is configured
  const isGoogleWorkspaceConfigured = orgDetails?.google_workspace_admin_user_id && 
                                    orgDetails?.google_admin_refresh_token;
  
  // Check if current user is the Google Workspace admin
  const isCurrentUserGoogleAdmin = isGoogleWorkspaceConfigured && 
                                   user?.id === orgDetails?.google_workspace_admin_user_id;

  if (isLoading || isLoadingOrgDetails) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-midnight font-heading">Domain Settings</h1>
        
        {/* Google Workspace Sync Button */}
        <Tooltip 
          content={!isGoogleWorkspaceConfigured 
            ? "You need to set a Google Workspace admin first" 
            : !isCurrentUserGoogleAdmin
              ? "Only the Google Workspace admin can sync the group"
              : "Sync all users to the Google Workspace group"}
          disabled={isGoogleWorkspaceConfigured && isCurrentUserGoogleAdmin}
        >
          <div>
            <Button 
              onClick={() => syncGoogleWorkspaceGroup.mutate()}
              disabled={!isGoogleWorkspaceConfigured || !isCurrentUserGoogleAdmin || syncGoogleWorkspaceGroup.isLoading}
              variant={isGoogleWorkspaceConfigured && isCurrentUserGoogleAdmin ? "primary" : "outline"}
            >
              {syncGoogleWorkspaceGroup.isLoading ? (
                <>
                <span className="flex items-center">
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </span>
              </>
              ) : (
              <>
                <span className="flex items-center">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sync Google Workspace Group
                </span>
              </>
              )}
              </Button>
          </div>
        </Tooltip>
      </div>

      {error && (
        <Alert type="error">
          {error}
        </Alert>
      )}

      {success && (
        <Alert type="success">
          Settings updated successfully
        </Alert>
      )}

      {syncMessage && (
        <Alert type={syncMessage.type}>
          {syncMessage.type === 'error' ? (
            <AlertCircle className="w-4 h-4 mr-2" />
          ) : null}
          {syncMessage.message}
        </Alert>
      )}

      {/* Google Workspace Admin Status */}
      <Card title="Google Workspace Integration">
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-midnight font-heading">Google Workspace Admin</h3>
              <p className="text-sm text-ebony">The admin user who manages Google Workspace integration</p>
            </div>
            
            <div className="flex items-center">
              {isGoogleWorkspaceConfigured ? (
                <Badge variant="success">Configured</Badge>
              ) : (
                <Badge variant="warning">Not Configured</Badge>
              )}
            </div>
          </div>
          
          {isGoogleWorkspaceConfigured ? (
            <div className="bg-old-lace/30 p-4 rounded-lg">
              <p className="text-sm">
                <span className="font-medium text-midnight">Current Admin:</span>{' '}
                {orgDetails?.google_workspace_admin_email || 'Unknown'}
                {isCurrentUserGoogleAdmin && (
                  <Badge variant="success" className="ml-2">You</Badge>
                )}
              </p>
              
              {isCurrentUserGoogleAdmin ? (
                <div className="mt-2">
                  <p className="text-xs text-ebony">
                    As the Google Workspace admin, you can sync organization users to your Google Workspace group.
                    <strong className="block mt-1 text-bittersweet">
                      Note: You must be an administrator in your Google Workspace domain to create and manage groups.
                    </strong>
                  </p>
                  <div className="mt-3 flex space-x-2">
                    <Button 
                      onClick={() => syncGoogleWorkspaceGroup.mutate()}
                      size="sm"
                      disabled={syncGoogleWorkspaceGroup.isLoading}
                    >
                      {syncGoogleWorkspaceGroup.isLoading ? 'Syncing...' : 'Sync Now'}
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm" 
                      onClick={() => window.open('https://admin.google.com/ac/groups', '_blank')}
                    >
                      Google Admin Console
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-ebony mt-1">
                  To change the Google Workspace admin, edit a user and check the "Google Workspace Admin" option.
                  Only the assigned Google Workspace admin can perform sync operations.
                  <strong className="block mt-1 text-bittersweet">
                    The Google Workspace admin must be an administrator in their Google Workspace domain.
                  </strong>
                </p>
              )}
            </div>
          ) : (
            <Alert type="info" className="mt-2">
              <Info className="w-4 h-4 mr-2" />
              <div>
                <p>To enable Google Workspace integration, edit a user and set them as the Google Workspace Admin.</p>
                <p className="text-xs mt-1 font-medium">
                  Important: This user must be an administrator in your Google Workspace domain.
                </p>
              </div>
            </Alert>
          )}
        </div>
      </Card>

      <form onSubmit={handleSubmit}>
        {/* Organization Details */}
        <Card title="Organization Details">
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-midnight mb-2">
                Organization Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-4 py-2 rounded-pill border-2 border-light-purple focus:border-sky-blue focus:ring focus:ring-sky-blue/20 transition-colors"
                placeholder="Enter organization name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-midnight mb-2">
                Industry
              </label>
              <select
                value={formData.industry || ''}
                onChange={(e) => handleInputChange('industry', e.target.value)}
                className="w-full px-4 py-2 rounded-pill border-2 border-light-purple focus:border-sky-blue focus:ring focus:ring-sky-blue/20 transition-colors"
              >
                <option value="">Select Industry</option>
                {INDUSTRIES.map((industry) => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-midnight mb-2">
                Employee Count Range
              </label>
              <select
                value={formData.employee_count_range || ''}
                onChange={(e) => handleInputChange('employee_count_range', e.target.value)}
                className="w-full px-4 py-2 rounded-pill border-2 border-light-purple focus:border-sky-blue focus:ring focus:ring-sky-blue/20 transition-colors"
              >
                <option value="">Select Employee Range</option>
                {EMPLOYEE_RANGES.map((range) => (
                  <option key={range} value={range}>
                    {range} employees
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end mt-6">
              <Button
                type="submit"
                disabled={updateMutation.isLoading}
              >
                {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Domain Information */}
        <Card title="Domain Settings" className="mt-6">
          <div className="p-6">
            <div>
              <label className="block text-sm font-medium text-midnight mb-2">
                Domain Name
              </label>
              <input
                type="text"
                value={orgData?.domain_name || 'No domain configured'}
                readOnly
                className="w-full px-4 py-2 rounded-pill border-2 border-light-purple bg-old-lace/30 text-ebony"
              />
              <div className="mt-2">
                {orgData?.domain_name ? (
                  <Badge 
                    variant={orgData?.domain_status === 'verified' ? 'success' : 'warning'}
                  >
                    {orgData?.domain_status === 'verified' ? 'Verified' : 'Pending Verification'}
                  </Badge>
                ) : (
                  <Badge variant="error">No Domain</Badge>
                )}
              </div>
            </div>
          </div>
        </Card>
      </form>
    </div>
  );
}